import React, { useState } from 'react';
import { HelpCircle, X, FileText, Video, Mail, ExternalLink } from 'lucide-react';
import TutorialModal from './TutorialModal';
import ContactSupportModal from './ContactSupportModal';
import { useI18n } from '../contexts/I18nContext';

const HelpWidget = () => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);

  const helpOptions = [
    {
      title: 'Quick Start Guide',
      description: 'Learn the basics in 5 minutes',
      icon: Video,
      action: () => {
        setIsTutorialOpen(true);
        setIsOpen(false); // Close help widget when tutorial opens
      },
      type: 'primary'
    },
    {
      title: 'Contact Support',
      description: 'Get help from our team',
      icon: Mail,
      action: () => {
        setContactSupportOpen(true);
        setIsOpen(false); // Close help widget when support opens
      },
      type: 'secondary'
    }
  ];

  const commonQuestions = [
    {
      question: 'How do I import my contacts?',
      answer: 'Go to Import/Export page and upload your CSV file. We support standard formats from most CRMs.',
      link: '/import-export'
    },
    {
      question: 'How do I create a deal?',
      answer: 'Use the Quick Add button or go to the Deals page and click "Add Deal".',
      link: '/deals'
    },
    {
      question: 'Can I upgrade my plan?',
      answer: 'Yes! Go to Settings to view and change your subscription plan anytime.',
      link: '/settings'
    }
  ];

  return (
    <>
      {/* Help Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className={`
            w-14 h-14 bg-[#0176D3] text-white rounded-full shadow-lg hover:shadow-xl 
            hover:bg-[#0155A8] transition-all duration-300 flex items-center justify-center
            hover:scale-110 ${isOpen ? 'scale-0' : 'scale-100'}
          `}
          aria-label="Open help"
        >
          <HelpCircle className="w-6 h-6" />
        </button>
      </div>

      {/* Help Panel */}
      <div className={`
        fixed bottom-6 right-6 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50
        transform transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
        }
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Help & Support</h3>
            <p className="text-sm text-gray-600">We're here to help you succeed</p>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close help"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {/* Quick Actions */}
          <div className="space-y-3 mb-6">
            {helpOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <button
                  key={index}
                  onClick={option.action}
                  className={`
                    w-full flex items-start gap-3 p-3 rounded-lg transition-all hover:shadow-sm
                    ${option.type === 'primary' ? 'bg-blue-50 hover:bg-blue-100 border border-blue-200' : ''}
                    ${option.type === 'secondary' ? 'bg-gray-50 hover:bg-gray-100' : ''}
                    ${option.type === 'accent' ? 'bg-green-50 hover:bg-green-100 border border-green-200' : ''}
                  `}
                >
                  <div className={`
                    p-2 rounded-lg flex-shrink-0
                    ${option.type === 'primary' ? 'bg-[#0176D3] text-white' : ''}
                    ${option.type === 'secondary' ? 'bg-gray-200 text-gray-700' : ''}
                    ${option.type === 'accent' ? 'bg-green-500 text-white' : ''}
                  `}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900 text-sm">{option.title}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
                </button>
              );
            })}
          </div>

          {/* Common Questions */}
          <div>
            <h4 className="font-medium text-gray-900 text-sm mb-3">Common Questions</h4>
            <div className="space-y-3">
              {commonQuestions.map((item, index) => (
                <div key={index} className="border border-gray-100 rounded-lg p-3">
                  <div className="font-medium text-gray-900 text-sm mb-1">{item.question}</div>
                  <div className="text-xs text-gray-600 mb-2">{item.answer}</div>
                  <button
                    onClick={() => window.location.href = item.link}
                    className="text-xs text-[#0176D3] hover:text-[#0155A8] font-medium flex items-center gap-1"
                  >
                    Go to page <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              Need more help? Email us at{' '}
              <a 
                href="mailto:support@bissa.ai" 
                className="text-[#0176D3] hover:text-[#0155A8] font-medium"
              >
                support@bissa.ai
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Tutorial Modal */}
      <TutorialModal 
        isOpen={isTutorialOpen}
        onClose={() => setIsTutorialOpen(false)}
      />

      {/* Contact Support Modal */}
      <ContactSupportModal 
        isOpen={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
        user={null} // HelpWidget doesn't have access to user, but modal handles it
      />
    </>
  );
};

export default HelpWidget;